// stereo-core: orientation conventions.
// Planes: dip (0-90), dip direction (0-360). Lines: trend (0-360), plunge (-90..90, + down).
// Copyright (C) 2026 DipField contributors. GPL-3.0-or-later.
import { Vec3, DEG, unit } from "./vec.js";

export const wrap360 = (a: number) => ((a % 360) + 360) % 360;

// [ORI-01]
/** Downward-pointing pole (lower-hemisphere normal) of a plane. */
export function planeToPole(dip: number, dipDir: number): Vec3 {
  const d = dip * DEG, dd = dipDir * DEG;
  return [-Math.sin(d) * Math.sin(dd), -Math.sin(d) * Math.cos(dd), -Math.cos(d)];
}
// [ORI-02]
/** Unit vector of a line, plunge positive downward. */
export function lineToVec(trend: number, plunge: number): Vec3 {
  const t = trend * DEG, p = plunge * DEG;
  return [Math.cos(p) * Math.sin(t), Math.cos(p) * Math.cos(t), -Math.sin(p)];
}
/** Trend/plunge of a vector, forced to the lower hemisphere (plunge >= 0). */
export function vecToLine(v: Vec3): { trend: number; plunge: number } {
  let [e, n, u] = unit(v);
  if (u > 0) { e = -e; n = -n; u = -u; }
  const plunge = Math.asin(Math.min(1, -u)) / DEG;
  const trend = Math.hypot(e, n) < 1e-12 ? 0 : wrap360(Math.atan2(e, n) / DEG);
  return { trend, plunge };
}
/** Plane (dip/dip direction) whose pole is `v` (either sense). */
export function poleToPlane(v: Vec3): { dip: number; dipDir: number } {
  const { trend, plunge } = vecToLine(v);
  return { dip: 90 - plunge, dipDir: wrap360(trend + 180) };
}
// [ORI-03]
/** Strike right-hand rule -> dip direction. */
export const strikeRHRToDipDir = (strike: number) => wrap360(strike + 90);
export const dipDirToStrikeRHR = (dipDir: number) => wrap360(dipDir - 90);
/** Strike left-hand rule -> dip direction. */
export const strikeLHRToDipDir = (strike: number) => wrap360(strike - 90);
export const dipDirToStrikeLHR = (dipDir: number) => wrap360(dipDir + 90);

/** Global orientation formats (as in DIPS project settings). Trend/plunge data
 *  are treated as pole vectors; internally every record is the plane whose pole
 *  it is, so all formats share one pipeline. */
export type OrientationFormat = "dip-dipdir" | "strike-rhr-dip" | "strike-lhr-dip" | "trend-plunge";
export const FORMAT_LABELS: Record<OrientationFormat, [string, string, string]> = {
  "dip-dipdir": ["Dip / Dip direction", "Dip", "Dip dir"],
  "strike-rhr-dip": ["Strike (right) / Dip", "Strike", "Dip"],
  "strike-lhr-dip": ["Strike (left) / Dip", "Strike", "Dip"],
  "trend-plunge": ["Trend / Plunge", "Trend", "Plunge"],
};
/** Plane (dip, dip direction) -> the two numbers shown in a format, in column order. */
export function planeToFormat(dip: number, dipDir: number, f: OrientationFormat): [number, number] {
  switch (f) {
    case "dip-dipdir": return [dip, dipDir];
    case "strike-rhr-dip": return [dipDirToStrikeRHR(dipDir), dip];
    case "strike-lhr-dip": return [dipDirToStrikeLHR(dipDir), dip];
    case "trend-plunge": return [wrap360(dipDir + 180), 90 - dip];
  }
}
// [ORI-03] [ORI-04]
/** The two numbers of a format (column order) -> plane. Returns null if out of range. */
export function formatToPlane(a: number, b: number, f: OrientationFormat): { dip: number; dipDir: number } | null {
  const inAz = (x: number) => isFinite(x) && x >= 0 && x <= 360;
  const inDip = (x: number) => isFinite(x) && x >= 0 && x <= 90;
  switch (f) {
    case "dip-dipdir": return inDip(a) && inAz(b) ? { dip: a, dipDir: wrap360(b) } : null;
    case "strike-rhr-dip": return inAz(a) && inDip(b) ? { dip: b, dipDir: strikeRHRToDipDir(a) } : null;
    case "strike-lhr-dip": return inAz(a) && inDip(b) ? { dip: b, dipDir: strikeLHRToDipDir(a) } : null;
    case "trend-plunge": {
      if (!inAz(a) || !isFinite(b) || b < -90 || b > 90) return null;
      const t = b < 0 ? wrap360(a + 180) : a, pl = Math.abs(b); // upward line -> its downward sense
      return { dip: 90 - pl, dipDir: wrap360(t + 180) };
    }
  }
}
// [ORI-05]
/** Line of intersection of two planes (lower hemisphere). */
export function intersection(p1: { dip: number; dipDir: number }, p2: { dip: number; dipDir: number }) {
  const a = planeToPole(p1.dip, p1.dipDir), b = planeToPole(p2.dip, p2.dipDir);
  const c: Vec3 = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  if (Math.hypot(...c) < 1e-9) return null; // parallel planes
  return vecToLine(c);
}
